import isAccountWebTransport from 'utils/accountWebTransport';
import * as FullStory from '@fullstory/browser';
import { setConfig as setApiConfig } from 'js-api-client';
import Cookies from 'js-cookie';
import coreApi from 'js-api-client/core';
import purchaseApi from '@reservamos/js-api-client/purchase';
import { browserName, deviceType, osName } from 'react-device-detect';
import OneSignal from 'react-onesignal';
import { matchPath } from 'react-router-dom';
import {
  analyticsReady,
  getDistinctId,
  load as loadSegment,
  trackEvent,
  identifyUser,
} from 'user-analytics';
import initMoment from 'utils/initMoment';
import { CurrentLang } from 'utils/lang';
import { getProductType } from 'utils/Reserbus';
import { REFERRAL_ROUTE_PATH } from 'utils/referralDestination';
import getMixpanelUserAgentProps from 'utils/userAgentProps';
import { isNativeAppWebView, isNativeIOSWebView, isNativeAndroidWebView } from 'utils/webview';
import readNativeAuthToken, { holdNativeAuthToken } from 'utils/nativeAuthToken';
import { holdClientApiKey } from 'utils/clientApiKey';
import reactNativeMessage from 'hooks/useReactNativeMessage';
import { selectBasisTheoryInitializationConfig } from 'selectors/basisTheorySelectors';
import { resolveBasisTheoryClient } from 'services/basisTheory';
import errorMonitoring from './errorMonitoring';
import adyen from '../payments/core/engines/adyen';
import cybersource from '../payments/core/engines/cybersource';
import { loadSearchWidgetScript } from './utils/searchWidget';
import kushki from '../payments/core/engines/kushki';
import growthBook from './services/growthBook';
import store from './store';
import loadBotlers from './utils/botlers';
import loadRiskified from './utils/loadRiskified';
import redirectHttps from './utils/redirectHttps';
import { captureCampaignAttribution } from './utils/campaignAttribution';
import { syncReferralAnalytics } from './utils/referralContext';
import { getSessionId, setInitialSessionId } from './utils/session';
import { initTaplytics } from './utils/taplytics';
import fingerprintService from './services/userFingerprint/userFingerprint';
import {
  initializeCommerceApiClient,
  setAuthToken as setCommerceAuthToken,
} from './services/commerce-api/commerceApiClient';
import cieloEngine from '../payments/core/engines/cielo';
import redeEngine from '../payments/core/engines/rede';
import stripeEngine from '../payments/core/engines/stripe';
import isReserhubUnifiedAccountEnabled, {
  isUnifiedAccountEnabledForClient,
} from './utils/isReserhubUnifiedAccountEnabled';
import {
  getRnMixpanelIdentifyTraits,
  getRnMixpanelNativeProps,
  getRnMixpanelPendingDistinctId,
} from './utils/rnMixpanelNativeProps';

// Used to distinguish between mobile and desktop client
const isDesktop = getProductType() === 'desktop';

// The api-key breadcrumbs below report a *static config shape*: the condition is identical for
// every session of a given tenant, so reporting on each boot repeats one signal at session
// volume, burying real errors and burning quota. Sampling is what actually bounds that volume —
// a per-page-load guard is a no-op (fresh JS context each load, and this module boots once per
// context) and storage-based dedup still scales with the number of devices.
const CONFIG_BREADCRUMB_SAMPLE_RATE = 0.01;

/**
 * Whether this boot should report a static api-key config breadcrumb. At any real traffic level
 * the misconfiguration still surfaces quickly, at ~1% of the reports.
 * @returns {boolean} True for roughly CONFIG_BREADCRUMB_SAMPLE_RATE of boots.
 */
const shouldReportConfigBreadcrumb = () => Math.random() < CONFIG_BREADCRUMB_SAMPLE_RATE;

/**
 * Initialize all the services needed using the tenant config
 * @param {Object} whitelabelConfig - tenant config
 * @returns {Promise} - Promise that resolves when all the services are initialized
 */
async function initializeClientServices(whitelabelConfig) {
  if (!window) return;
  const isReferralEntry = Boolean(
    matchPath(window.location.pathname, { path: REFERRAL_ROUTE_PATH, exact: true }),
  );

  const { env: whitelabelEnvs, features: whitelabelFeatures } = whitelabelConfig;

  // Push campaign attribution, captured here and not at purchase time: a new search rebuilds the
  // query string from scratch, so reading it late would silently drop the campaign for anyone who
  // re-searches before buying.
  if (whitelabelFeatures.SEND_CAMPAIGN_ATTRIBUTION_TO_PURCHASE) captureCampaignAttribution();

  // Load search widget style
  if (whitelabelEnvs.searchWidget.useDynamicLoad)
    loadSearchWidgetScript(
      whitelabelEnvs.searchWidget.version,
      whitelabelEnvs.searchWidget.useDynamicLoad,
    );

  redirectHttps();

  const storeInstance = store.createInstance({ whitelabelConfig });

  // moment
  initMoment(whitelabelEnvs.lang.default, whitelabelFeatures.HOUR_FORMAT);

  // session id used for antifraud beacons
  setInitialSessionId();

  const defaultLanguage = whitelabelEnvs.lang.default;
  const i18nextLng =
    typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('i18nextLng') || defaultLanguage
      : defaultLanguage;

  if (!whitelabelEnvs.api) throw new Error('whitelabelEnvs.api is undefined');

  let apiKey;
  let productType;

  /**
   * Set the API key and product type based on the device platform.
   * All native-app branches require the authoritative native_app_* UA identifier.
   * `api.nativeKeyPriorityEnabled` is a per-tenant rollout gate (MOB-515): iosKey/androidKey
   * have never been exercised in production for a tenant whose config still carries a
   * webviewKey, and `productType` also feeds GrowthBook targeting, Mixpanel super props,
   * and the fingerprint tag — flipping it can silently stop matching existing rules there.
   * With the gate off (the default), classification is byte-for-byte what it was before
   * MOB-515. Enable it per tenant in whitelabel config only once its iosKey/androidKey are
   * confirmed active in the backend and any downstream rule keyed on product=="webview" has
   * been reviewed.
   *
   * Migrate both keys together: with the gate on, a tenant that has only one of
   * iosKey/androidKey configured ends up split — one platform reports "ios"/"android"
   * while the other still falls through to "webview" for the same app. That's only
   * expected as a brief, deliberate transition; treat a half-migrated tenant left in
   * that state as a config bug, not a valid end state.
   *
   * This duplicates the full chain (gated vs. legacy) rather than collapsing it, so both
   * copies must stay in sync until every tenant has migrated — see MOB-521 to retire the
   * legacy branch and the flag itself once that happens.
   * @deprecated Use `iosKey` and `androidKey` instead of `webviewKey`, generic webview detection will be removed in the near future.
   */
  if (whitelabelEnvs.api?.nativeKeyPriorityEnabled) {
    if (isNativeIOSWebView() && whitelabelEnvs.api?.iosKey) {
      apiKey = whitelabelEnvs.api.iosKey;
      productType = 'ios';
    } else if (isNativeAndroidWebView() && whitelabelEnvs.api?.androidKey) {
      apiKey = whitelabelEnvs.api.androidKey;
      productType = 'android';
    } else if (isNativeAppWebView() && whitelabelEnvs.api?.webviewKey) {
      apiKey = whitelabelEnvs.api.webviewKey;
      productType = 'webview';
    } else if (isDesktop && whitelabelEnvs.api?.desktopKey) {
      apiKey = whitelabelEnvs.api.desktopKey;
      productType = 'web';
    } else {
      apiKey = whitelabelEnvs.api.mobileKey;
      productType = 'web-mobile';
    }
  } else if (isNativeAppWebView() && whitelabelEnvs.api?.webviewKey) {
    apiKey = whitelabelEnvs.api.webviewKey;
    productType = 'webview';
  } else if (isNativeIOSWebView() && whitelabelEnvs.api?.iosKey) {
    apiKey = whitelabelEnvs.api.iosKey;
    productType = 'ios';
  } else if (isNativeAndroidWebView() && whitelabelEnvs.api?.androidKey) {
    apiKey = whitelabelEnvs.api.androidKey;
    productType = 'android';
  } else if (isDesktop && whitelabelEnvs.api?.desktopKey) {
    apiKey = whitelabelEnvs.api.desktopKey;
    productType = 'web';
  } else {
    apiKey = whitelabelEnvs.api.mobileKey;
    productType = 'web-mobile';
  }

  holdClientApiKey(apiKey);

  // A native app UA that matched none of the native-specific branches falls through to
  // desktopKey/mobileKey silently — that's an in-app purchase misclassified as generic
  // web with no error anywhere. Surface it so a missing/typo'd key gets caught quickly.
  // Deliberately not gated behind nativeKeyPriorityEnabled: this same silent fallthrough
  // was already possible before MOB-515 for any tenant missing a legacy webviewKey.
  if (
    isNativeAppWebView() &&
    productType !== 'ios' &&
    productType !== 'android' &&
    productType !== 'webview' &&
    shouldReportConfigBreadcrumb()
  ) {
    errorMonitoring.notify({
      error: new Error('Native app WebView detected but no iosKey/androidKey/webviewKey matched'),
      info: {
        phase: 'initialization',
        service: 'apiKey selection',
        brand: whitelabelEnvs.brand,
      },
    });
  }

  // Gate on with only one of iosKey/androidKey configured resolves "successfully" to the
  // legacy webviewKey branch — identical to a tenant that hasn't migrated at all, so the
  // half-migrated state above has no signal of its own. Flag it separately so it doesn't
  // depend on someone reading the JSDoc before flipping the gate.
  if (
    whitelabelEnvs.api?.nativeKeyPriorityEnabled &&
    isNativeAppWebView() &&
    Boolean(whitelabelEnvs.api?.iosKey) !== Boolean(whitelabelEnvs.api?.androidKey) &&
    shouldReportConfigBreadcrumb()
  ) {
    errorMonitoring.notify({
      error: new Error(
        'nativeKeyPriorityEnabled is on but only one of iosKey/androidKey is configured',
      ),
      info: {
        phase: 'initialization',
        service: 'apiKey selection',
        brand: whitelabelEnvs.brand,
      },
    });
  }

  const reserhubUnifiedAccountEnabled = isReserhubUnifiedAccountEnabled(
    whitelabelEnvs.reserhub?.account,
    {
      // GrowthBook may not be initialized yet; `true` avoids blocking progressiveRollout accounts at boot
      unifiedAccountFeature: true,
    },
  );

  const reserhubAccountHasProgressiveRollout = whitelabelEnvs.reserhub?.account?.progressiveRollout;
  const useClassicToken = !(reserhubUnifiedAccountEnabled && !reserhubAccountHasProgressiveRollout);

  // Same boot-time GrowthBook assumption as above, plus the native-app opt-in. Resolved separately
  // from `reserhubUnifiedAccountEnabled` because that one drives cookie/classic-token behavior, which
  // is tenant-level; the bearer credential is client-level and must respect the WebView gate.
  const unifiedAccountEnabledForClient = isUnifiedAccountEnabledForClient(
    whitelabelEnvs.reserhub?.account,
    {
      unifiedAccountFeature: true,
      features: whitelabelFeatures,
      brand: whitelabelEnvs.brand,
    },
  );

  // Reservamos API Client
  setApiConfig({
    searchUrl: whitelabelEnvs.api.searchUrl,
    purchaseUrl: whitelabelEnvs.api.purchaseUrl,
    apiKey,
    userAuthToken: useClassicToken ? Cookies.get('token') : undefined,
    headers: {
      'accept-language': i18nextLng,
    },
    withCredentials: reserhubUnifiedAccountEnabled,
  });
  purchaseApi.setConfig({
    baseUrl: whitelabelEnvs.api.purchaseUrl,
    defaultHeaders: {
      Origin: origin,
      Authorization: `Token token=${apiKey}`,
    },
  });
  coreApi.setConfig({
    baseUrl: whitelabelEnvs.coreApi?.url,
    defaultHeaders: {
      Origin: origin,
      Authorization: `Bearer ${apiKey}`,
    },
  });

  // Initialize the generated Commerce API client
  initializeCommerceApiClient();

  // Adopt the account token the native app publishes before this bundle evaluates, so the account
  // provider's first fetch on mount already carries it. Returns null outside the native app.
  //
  // Read unconditionally, gate afterwards: reading is what consumes the global, and a tenant that
  // has not opted in is exactly the case where a live credential must not be left on `window` for
  // page scripts to replay. The gate then withholds it from the client, because the commerce client
  // attaches the bearer to every call it makes — including commerce paths such as installments
  // validation, which never pass through the account gate.
  const publishedNativeToken = readNativeAuthToken();
  const rolloutStillPending = Boolean(whitelabelEnvs.reserhub?.account?.progressiveRollout);

  if (!unifiedAccountEnabledForClient || isAccountWebTransport(whitelabelEnvs.brand)) {
    setCommerceAuthToken(null);
  } else if (rolloutStillPending) {
    // `unifiedAccountEnabledForClient` assumed the experiment includes this visitor, because
    // GrowthBook has not loaded yet. Attaching the bearer on that assumption would let a visitor the
    // rollout excludes authenticate commerce traffic during the boot window — the client attaches it
    // to every endpoint — and clearing it once the real value arrives cannot un-send a request
    // already issued. Hold it instead: no account request happens before then either, since
    // `accountEnabled` stays false for a progressive tenant until GrowthBook resolves.
    holdNativeAuthToken(publishedNativeToken);
    setCommerceAuthToken(null);
  } else {
    setCommerceAuthToken(publishedNativeToken);
  }

  // conekta
  if (window.Conekta) {
    window.Conekta.setPublicKey(whitelabelEnvs.conekta.pubKey);
  }

  // GrowthBook
  const { brand, growthBook: growthBookConfig } = whitelabelEnvs;

  const browser = browserName.toLowerCase();
  const device = deviceType.toLowerCase();
  const os = osName.toLowerCase();

  if (growthBookConfig?.enabled) {
    growthBook.init({
      enableDevMode: growthBookConfig.enableDevMode,
      trackingCallback: (experiment, result) => {
        // Send the experiment data to mixpanel
        trackEvent('$experiment_started', {
          'Experiment name': experiment.key,
          'Variant value': result.value,
          '$source': 'growthbook',
        });
      },
    });
    growthBook.setAttributes({
      brand,
      browser,
      device,
      os,
      product: productType,
    });
    growthBook.loadFeatures(growthBookConfig.featuresEndpoint);
  }

  // User Fingerprint
  const { userFingerprint } = whitelabelEnvs;

  if (userFingerprint?.enabled) {
    await fingerprintService.init(userFingerprint);
    fingerprintService.setUserMetadata({
      tag: {
        tenant: brand,
        productType,
      },
    });
  }

  /**
   * Returns the super props to sent in all the mixpanel events.
   * When running inside a native app WebView, saas-app injects super props before content
   * loads and registers them via applyRnMixpanelBridge when Segment loads.
   * @returns {Object} superProps
   */
  const getMixpanelSuperProps = () => {
    const superProps = { product: productType };

    if (whitelabelFeatures.REGISTER_USER_AGENT_PROPS_INTO_MIXPANEL) {
      return getMixpanelUserAgentProps(superProps);
    }

    return superProps;
  };

  const mixpanelSuperProps = getMixpanelSuperProps();

  /**
   * Register fingerprint into the Mixpanel superProps
   */
  const syncFingerprintAndMixpanel = async ({ distinctId }) => {
    // The fingerprint is gotten only if we are in the initial pages to save resources
    const pathValidForGetFingerprint =
      window.location.pathname.includes('/search') ||
      isReferralEntry ||
      window.location.pathname === '/';

    // Adding device fingerprint to mixpanel super pros and distinct id to user fingerprint
    fingerprintService.setUserMetadata({
      linkedId: distinctId,
    });
    window.mixpanel?.unregister?.('Visitor Found');

    let userFingerprintData;
    if (pathValidForGetFingerprint) {
      userFingerprintData = await fingerprintService.setUpFingerprint();
    } else {
      userFingerprintData = await fingerprintService.getCachedFingerprint();
    }
    if (userFingerprintData?.visitorId) {
      const fingerprintSuperProperties = {
        [fingerprintService.getTrackingKey()]: userFingerprintData.visitorId,
      };
      window.mixpanel.register(fingerprintSuperProperties);
    } else {
      window.mixpanel?.unregister?.(fingerprintService.getTrackingKey());
    }
  };

  /**
   * Synchronizes the external user ID with Mixpanel and returns the user ID
   */
  function syncExternalUserId() {
    const queryString = new URLSearchParams(window.location.search);
    const userId = queryString.get('userId');

    if (userId) {
      identifyUser(userId, { 'User Type': 'Anonymous' });
    }

    return userId || getDistinctId();
  }

  /**
   * Applies native Mixpanel super props and distinct id bridged from saas-app (early injection
   * or postMessage). Search / View Results can fire before loadEnd; early globals + explicit
   * merge in search trackers cover that window.
   */
  const applyRnMixpanelBridge = ({ distinctId, props } = {}) => {
    const bridgedProps = props || getRnMixpanelNativeProps();
    if (bridgedProps && typeof bridgedProps === 'object' && Object.keys(bridgedProps).length) {
      // eslint-disable-next-line no-underscore-dangle
      window.__RN_MIXPANEL_NATIVE_PROPS__ = bridgedProps;
      if (window.mixpanel?.register) {
        window.mixpanel.register(bridgedProps);
      }
    }

    const bridgedDistinctId = distinctId || getRnMixpanelPendingDistinctId() || null;
    if (bridgedDistinctId) {
      identifyUser(bridgedDistinctId, getRnMixpanelIdentifyTraits());
      // eslint-disable-next-line no-underscore-dangle
      window.__RN_MIXPANEL_IDENTIFIED_AS__ = bridgedDistinctId;
    }
  };

  /**
   * Function executed when the tracking engine is loaded
   */
  const onLoadedSegment = () => {
    syncReferralAnalytics();
    if (isNativeAppWebView()) {
      applyRnMixpanelBridge();
    }

    const distinctId = syncExternalUserId();
    syncFingerprintAndMixpanel({ distinctId });

    if (growthBookConfig?.enabled) {
      growthBook.setAttributes({ id: distinctId, isEligibleForQuickPurchase: false });
    }

    // This is done to make know another libraries that mixpanel is ready to be used
    window.dispatchEvent(new CustomEvent('MixpanelReady'));
  };

  // segment
  loadSegment(whitelabelEnvs.segment.token, {
    ...whitelabelEnvs.segment.options,
    superProps: mixpanelSuperProps,
    onLoaded: onLoadedSegment,
  });

  // saas-app injects __RN_MIXPANEL_NATIVE_PROPS__ before content loads and posts
  // setMpDistinctId after loadEnd (refresh $browser, re-identify).
  if (isNativeAppWebView()) {
    analyticsReady(() => {
      const RNMessage = reactNativeMessage();
      RNMessage.listen(({ data }) => {
        const { action, message, props } = data || {};
        if (action !== 'setMpDistinctId') return;
        applyRnMixpanelBridge({ distinctId: message, props });
      });
    });
  }

  if (whitelabelEnvs.fullstory?.enabled) {
    FullStory.init({
      orgId: whitelabelEnvs.fullstory.orgId,
      debug: !__PROD__,
    });
  }

  // cybersource
  if (whitelabelEnvs.cybersource?.enabled) {
    const { orgId, merchantId, isSandbox, clientLibrary } = whitelabelEnvs.cybersource;
    cybersource.loadCybersourceFlex(clientLibrary)();
    if (!whitelabelFeatures.DYNAMIC_CYBERSOURCE_CREDENTIALS) {
      cybersource.initialize({
        orgId,
        merchantId,
        isSandbox,
        sessionId: getSessionId(),
      });
    }
  }

  // riskified
  loadRiskified(whitelabelEnvs.riskified);

  if (whitelabelEnvs.botlers && whitelabelEnvs.botlers.enabled) {
    loadBotlers(whitelabelEnvs.botlers.bmid);
  }

  if (whitelabelEnvs.kushki && whitelabelEnvs.kushki.enabled) {
    const { merchantId, isSandbox } = whitelabelEnvs.kushki;
    kushki.initialize({ merchantId, inTestEnvironment: isSandbox });
  }

  if (whitelabelEnvs.adyen && whitelabelEnvs.adyen.enabled) {
    if (
      whitelabelEnvs.adyen.creditCard?.enabled ||
      whitelabelEnvs.adyen.applePay?.enabled ||
      whitelabelEnvs.adyen.googlePay?.enabled
    ) {
      adyen.initialize({
        ...whitelabelEnvs,
        lang: { current: CurrentLang(), default: whitelabelEnvs.lang.default },
      });
    }
  }

  if (whitelabelEnvs.cielo?.enabled) {
    const { tokenizeEndpoint, threeDSTokenEndpoint, sopAccessTokenEndpoint } = whitelabelEnvs.cielo;
    cieloEngine.init({ tokenizeEndpoint, threeDSTokenEndpoint, sopAccessTokenEndpoint });
  }

  if (whitelabelEnvs.rede?.enabled) {
    const { tokenizeEndpoint } = whitelabelEnvs.rede;
    redeEngine.init({ tokenizeEndpoint });
  }

  if (whitelabelEnvs.stripe?.enabled) {
    stripeEngine.initialize({
      ...whitelabelEnvs,
      locale: CurrentLang(),
    });
  }

  const basisTheoryConfig = selectBasisTheoryInitializationConfig(storeInstance.getState());
  if (basisTheoryConfig.enabled) {
    resolveBasisTheoryClient(basisTheoryConfig.publicKey).catch((error) => {
      errorMonitoring.notify({
        error,
        info: {
          phase: 'initialization',
          service: 'Basis Theory',
        },
      });
    });
  }

  // One Signal — production apps are domain-restricted; skip on local funnel hosts.
  const { hostname } = window.location;
  const isLocalFunnelHost =
    hostname === 'localhost' ||
    hostname.endsWith('.platform.localhost') ||
    hostname.endsWith('.platform.test');

  if (whitelabelEnvs.oneSignal.enabled && !isLocalFunnelHost) {
    OneSignal.init({
      appId: whitelabelEnvs.oneSignal.appId,
      notifyButton: {
        enable: true,
      },
    });
  }

  // Taplytics
  initTaplytics(whitelabelEnvs.taplytics);

  return { storeInstance };
}

export default initializeClientServices;
